import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phone-input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { LogoUpload } from '@/components/LogoUpload'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { WebsitePage } from './WebsitePage'
import { BrandingPage } from './BrandingPage'
import { useSettingsTab } from './useSettingsTab'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Plus, Trash2, MapPin, Star, Globe, ExternalLink, Copy, Check } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import type { BusinessHour, ServiceArea } from '@limo/database'

// Days of the week
const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
]

// Common US timezones
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Phoenix', label: 'Arizona (MST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
  { value: 'America/Puerto_Rico', label: 'Atlantic Time (AST)' },
  // Dev-only timezones
  ...(import.meta.env.DEV ? [
    { value: 'Africa/Lagos', label: 'West Africa Time (WAT)' },
  ] : []),
]

// Business Info Schema
const businessInfoSchema = z.object({
  name: z.string().min(1, 'Business name is required'),
  business_email: z.string().email('Invalid email').optional().or(z.literal('')),
  business_phone: z.string().optional(),
  custom_domain: z.string()
    .transform(val => val?.toLowerCase().trim())
    .refine(val => !val || /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(val), {
      message: 'Enter a valid domain (e.g., book.yourcompany.com)',
    })
    .optional()
    .or(z.literal('')),
  timezone: z.string().optional(),
  address_line1: z.string().optional(),
  address_line2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postal_code: z.string().optional(),
  country: z.string().optional(),
  show_address_in_footer: z.boolean().default(false),
})

// Account setting key controlling the footer address (stored in account_settings)
const SHOW_ADDRESS_IN_FOOTER_KEY = 'show_address_in_footer'

type BusinessInfoFormData = z.infer<typeof businessInfoSchema>

// Service Area Schema
const serviceAreaSchema = z.object({
  name: z.string().min(1, 'Area name is required'),
  description: z.string().optional(),
  is_primary: z.boolean().default(false),
})

type ServiceAreaFormData = z.infer<typeof serviceAreaSchema>

export function BusinessProfilePage() {
  const [activeTab, setActiveTab] = useSettingsTab('info')
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [addingArea, setAddingArea] = useState(false)
  const [logoUrl, setLogoUrl] = useState<string | null>(null)

  const activeAccount = isImpersonating ? impersonatedAccount : account

  // Fetch account details
  const { data: accountData, isLoading: isLoadingAccount } = useQuery({
    queryKey: ['account', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return null

      const { data, error } = await supabase
        .from('accounts')
        .select('*')
        .eq('id', activeAccount.id)
        .single()

      if (error) throw error
      return data
    },
    enabled: !!activeAccount?.id,
  })

  // Fetch the footer-address setting (account_settings key)
  const { data: showAddressSetting } = useQuery({
    queryKey: ['account-setting', activeAccount?.id, SHOW_ADDRESS_IN_FOOTER_KEY],
    queryFn: async () => {
      if (!activeAccount?.id) return false

      const { data, error } = await supabase
        .from('account_settings')
        .select('value')
        .eq('account_id', activeAccount.id)
        .eq('key', SHOW_ADDRESS_IN_FOOTER_KEY)
        .maybeSingle()

      if (error) throw error
      return data?.value === 'true'
    },
    enabled: !!activeAccount?.id,
  })

  // Fetch business hours
  const { data: businessHours, isLoading: isLoadingHours } = useQuery({
    queryKey: ['business-hours', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []

      const { data, error } = await supabase
        .from('business_hours')
        .select('*')
        .eq('account_id', activeAccount.id)
        .order('day_of_week')

      if (error) throw error
      return data as BusinessHour[]
    },
    enabled: !!activeAccount?.id,
  })

  // Fetch service areas
  const { data: serviceAreas, isLoading: isLoadingAreas } = useQuery({
    queryKey: ['service-areas', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []

      const { data, error } = await supabase
        .from('service_areas')
        .select('*')
        .eq('account_id', activeAccount.id)
        .order('display_order')

      if (error) throw error
      return data as ServiceArea[]
    },
    enabled: !!activeAccount?.id,
  })

  const [copiedDomain, setCopiedDomain] = useState(false)

  // Business Info Form
  const businessInfoForm = useForm<BusinessInfoFormData>({
    resolver: zodResolver(businessInfoSchema),
    defaultValues: {
      name: '',
      business_email: '',
      business_phone: '',
      custom_domain: '',
      timezone: 'America/New_York',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US',
      show_address_in_footer: false,
    },
  })

  // Service Area Form
  const serviceAreaForm = useForm<ServiceAreaFormData>({
    resolver: zodResolver(serviceAreaSchema),
    defaultValues: {
      name: '',
      description: '',
      is_primary: false,
    },
  })

  // Update form when account data is loaded
  useEffect(() => {
    if (accountData) {
      businessInfoForm.reset({
        name: accountData.name || '',
        business_email: accountData.business_email || '',
        business_phone: accountData.business_phone || '',
        custom_domain: accountData.custom_domain || '',
        timezone: accountData.timezone || 'America/New_York',
        address_line1: accountData.address_line1 || '',
        address_line2: accountData.address_line2 || '',
        city: accountData.city || '',
        state: accountData.state || '',
        postal_code: accountData.postal_code || '',
        country: accountData.country || 'US',
        show_address_in_footer: showAddressSetting ?? false,
      })
      setLogoUrl(accountData.logo_url || null)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountData, showAddressSetting])

  // Logo update mutation
  const logoMutation = useMutation({
    mutationFn: async (url: string | null) => {
      if (!activeAccount?.id) throw new Error('No account found')

      const { error } = await supabase
        .from('accounts')
        .update({ logo_url: url })
        .eq('id', activeAccount.id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', activeAccount?.id] })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Handle logo change
  const handleLogoChange = (url: string | null) => {
    setLogoUrl(url)
    logoMutation.mutate(url)
  }

  // Business Info Mutation
  const businessInfoMutation = useMutation({
    mutationFn: async (data: BusinessInfoFormData) => {
      if (!activeAccount?.id) throw new Error('No account found')

      const { error } = await supabase
        .from('accounts')
        .update({
          name: data.name,
          business_email: data.business_email || null,
          business_phone: data.business_phone || null,
          custom_domain: data.custom_domain || null,
          timezone: data.timezone || 'America/New_York',
          address_line1: data.address_line1 || null,
          address_line2: data.address_line2 || null,
          city: data.city || null,
          state: data.state || null,
          postal_code: data.postal_code || null,
          country: data.country || 'US',
        })
        .eq('id', activeAccount.id)

      if (error) throw error

      // Persist the footer-address toggle in account_settings
      const { error: settingError } = await supabase
        .from('account_settings')
        .upsert(
          {
            account_id: activeAccount.id,
            key: SHOW_ADDRESS_IN_FOOTER_KEY,
            value: data.show_address_in_footer ? 'true' : 'false',
            description: 'Show business address in website footer',
            is_secret: false,
          },
          { onConflict: 'account_id,key' }
        )

      if (settingError) throw settingError
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', activeAccount?.id] })
      queryClient.invalidateQueries({
        queryKey: ['account-setting', activeAccount?.id, SHOW_ADDRESS_IN_FOOTER_KEY],
      })
      toast({
        title: 'Business profile updated',
        description: 'Your business information has been saved.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Business Hours Mutation
  const businessHoursMutation = useMutation({
    mutationFn: async (hours: { day_of_week: number; is_open: boolean; open_time: string | null; close_time: string | null }) => {
      if (!activeAccount?.id) throw new Error('No account found')

      const { error } = await supabase
        .from('business_hours')
        .upsert({
          account_id: activeAccount.id,
          day_of_week: hours.day_of_week,
          is_open: hours.is_open,
          open_time: hours.open_time,
          close_time: hours.close_time,
        }, { onConflict: 'account_id,day_of_week' })

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-hours', activeAccount?.id] })
      toast({
        title: 'Hours updated',
        description: 'Business hours have been saved.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Add Service Area Mutation
  const addServiceAreaMutation = useMutation({
    mutationFn: async (data: ServiceAreaFormData) => {
      if (!activeAccount?.id) throw new Error('No account found')

      // If marking as primary, unset other primary areas
      if (data.is_primary) {
        await supabase
          .from('service_areas')
          .update({ is_primary: false })
          .eq('account_id', activeAccount.id)
      }

      const { error } = await supabase
        .from('service_areas')
        .insert({
          account_id: activeAccount.id,
          name: data.name,
          description: data.description || null,
          is_primary: data.is_primary,
          display_order: (serviceAreas?.length || 0),
        })

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-areas', activeAccount?.id] })
      serviceAreaForm.reset()
      setAddingArea(false)
      toast({
        title: 'Service area added',
        description: 'New service area has been added.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Delete Service Area Mutation
  const deleteServiceAreaMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('service_areas')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-areas', activeAccount?.id] })
      toast({
        title: 'Service area removed',
        description: 'The service area has been removed.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Toggle Primary Service Area
  const togglePrimaryMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!activeAccount?.id) throw new Error('No account found')

      // First, unset all primary areas
      await supabase
        .from('service_areas')
        .update({ is_primary: false })
        .eq('account_id', activeAccount.id)

      // Then set the selected area as primary
      const { error } = await supabase
        .from('service_areas')
        .update({ is_primary: true })
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-areas', activeAccount?.id] })
      toast({
        title: 'Primary area updated',
        description: 'Primary service area has been set.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Get hours for a specific day
  const getHoursForDay = (dayOfWeek: number) => {
    return businessHours?.find(h => h.day_of_week === dayOfWeek) || {
      day_of_week: dayOfWeek,
      is_open: true,
      open_time: '09:00',
      close_time: '17:00',
    }
  }

  const isLoading = isLoadingAccount || isLoadingHours || isLoadingAreas

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-6 w-32" />
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-24" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="info">Business Info</TabsTrigger>
          <TabsTrigger value="hours">Business Hours</TabsTrigger>
          <TabsTrigger value="areas">Service Areas</TabsTrigger>
          <TabsTrigger value="website">Website</TabsTrigger>
          <TabsTrigger value="branding">Branding</TabsTrigger>
        </TabsList>

        {/* Business Info Tab */}
        <TabsContent value="info">
          <Card>
            <CardHeader>
              <CardTitle>Business Information</CardTitle>
              <CardDescription>
                Your company contact details and address
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...businessInfoForm}>
                <form
                  onSubmit={businessInfoForm.handleSubmit((data) => businessInfoMutation.mutate(data))}
                  className="space-y-6"
                >
                  {/* Logo Upload */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Business Logo</label>
                    <LogoUpload
                      value={logoUrl}
                      onChange={handleLogoChange}
                      accountId={activeAccount?.id || ''}
                      disabled={!activeAccount?.id}
                    />
                    <p className="text-xs text-muted-foreground">
                      Upload your business logo. PNG, JPG, WebP, or SVG up to 2MB.
                    </p>
                  </div>

                  <FormField
                    control={businessInfoForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Business Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Acme Limousine Services" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={businessInfoForm.control}
                      name="business_email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Business Email</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="info@company.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={businessInfoForm.control}
                      name="business_phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Business Phone</FormLabel>
                          <FormControl>
                            <PhoneInput placeholder="(555) 123-4567" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Custom Domain Section */}
                  <div className="rounded-lg border p-4 space-y-4">
                    <div className="flex items-center gap-2">
                      <Globe className="h-5 w-5 text-muted-foreground" />
                      <h4 className="font-medium">Custom Domain</h4>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-muted-foreground">Default URL:</span>
                        <code className="rounded bg-muted px-2 py-0.5 text-xs">
                          {activeAccount?.slug || accountData?.slug}.bookedridespages.com
                        </code>
                        <a
                          href={`https://${activeAccount?.slug || accountData?.slug}.bookedridespages.com`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </div>

                      <FormField
                        control={businessInfoForm.control}
                        name="custom_domain"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Custom Domain (Optional)</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="book.yourcompany.com"
                                {...field}
                              />
                            </FormControl>
                            <FormDescription>
                              Use your own domain for your booking site
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {businessInfoForm.watch('custom_domain') && (
                        <div className="rounded-lg bg-muted/50 p-3 space-y-2">
                          <p className="text-sm font-medium">DNS Configuration Required</p>
                          <p className="text-xs text-muted-foreground">
                            Add this CNAME record to your domain's DNS settings:
                          </p>
                          <div className="flex items-center gap-2 rounded bg-background p-2">
                            <code className="flex-1 text-xs font-mono">
                              CNAME → cname.vercel-dns.com
                            </code>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => {
                                navigator.clipboard.writeText('cname.vercel-dns.com')
                                setCopiedDomain(true)
                                setTimeout(() => setCopiedDomain(false), 2000)
                              }}
                            >
                              {copiedDomain ? (
                                <Check className="h-3.5 w-3.5 text-green-500" />
                              ) : (
                                <Copy className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            After saving, contact support to complete the domain setup.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <FormField
                    control={businessInfoForm.control}
                    name="timezone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Timezone</FormLabel>
                        <Select
                          key={`timezone-${field.value || 'default'}`}
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select timezone" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {TIMEZONES.map((tz) => (
                              <SelectItem key={tz.value} value={tz.value}>
                                {tz.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Your business timezone for scheduling and display
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={businessInfoForm.control}
                    name="address_line1"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address Line 1</FormLabel>
                        <FormControl>
                          <Input placeholder="123 Main Street" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={businessInfoForm.control}
                    name="address_line2"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address Line 2</FormLabel>
                        <FormControl>
                          <Input placeholder="Suite 100" {...field} />
                        </FormControl>
                        <FormDescription>
                          Apartment, suite, unit, building, floor, etc.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <FormField
                      control={businessInfoForm.control}
                      name="city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>City</FormLabel>
                          <FormControl>
                            <Input placeholder="New York" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={businessInfoForm.control}
                      name="state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>State</FormLabel>
                          <FormControl>
                            <Input placeholder="NY" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={businessInfoForm.control}
                      name="postal_code"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Postal Code</FormLabel>
                          <FormControl>
                            <Input placeholder="10001" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={businessInfoForm.control}
                      name="country"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Country</FormLabel>
                          <FormControl>
                            <Input placeholder="US" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={businessInfoForm.control}
                    name="show_address_in_footer"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel>Show address in website footer</FormLabel>
                          <FormDescription>
                            Display your business address in the footer of your
                            public website.
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <Button type="submit" disabled={businessInfoMutation.isPending}>
                    {businessInfoMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Save Changes
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Business Hours Tab */}
        <TabsContent value="hours">
          <Card>
            <CardHeader>
              <CardTitle>Business Hours</CardTitle>
              <CardDescription>
                Set your operating hours for each day of the week
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {DAYS_OF_WEEK.map((day) => {
                  const hours = getHoursForDay(day.value)
                  return (
                    <div
                      key={day.value}
                      className="flex flex-col gap-4 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex items-center gap-4">
                        <Switch
                          checked={hours.is_open}
                          onCheckedChange={(checked) => {
                            businessHoursMutation.mutate({
                              day_of_week: day.value,
                              is_open: checked,
                              open_time: checked ? (hours.open_time || '09:00') : null,
                              close_time: checked ? (hours.close_time || '17:00') : null,
                            })
                          }}
                        />
                        <span className="w-24 font-medium">{day.label}</span>
                      </div>

                      {hours.is_open ? (
                        <div className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={hours.open_time || '09:00'}
                            className="w-32"
                            onChange={(e) => {
                              businessHoursMutation.mutate({
                                day_of_week: day.value,
                                is_open: true,
                                open_time: e.target.value,
                                close_time: hours.close_time || '17:00',
                              })
                            }}
                          />
                          <span className="text-muted-foreground">to</span>
                          <Input
                            type="time"
                            value={hours.close_time || '17:00'}
                            className="w-32"
                            onChange={(e) => {
                              businessHoursMutation.mutate({
                                day_of_week: day.value,
                                is_open: true,
                                open_time: hours.open_time || '09:00',
                                close_time: e.target.value,
                              })
                            }}
                          />
                        </div>
                      ) : (
                        <span className="text-muted-foreground">Closed</span>
                      )}
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Service Areas Tab */}
        <TabsContent value="areas">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Service Areas</CardTitle>
                <CardDescription>
                  Cities and regions where you provide service
                </CardDescription>
              </div>
              {!addingArea && (
                <Button onClick={() => setAddingArea(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Area
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {addingArea && (
                <Card className="mb-6 border-dashed">
                  <CardContent className="pt-6">
                    <Form {...serviceAreaForm}>
                      <form
                        onSubmit={serviceAreaForm.handleSubmit((data) => addServiceAreaMutation.mutate(data))}
                        className="space-y-4"
                      >
                        <FormField
                          control={serviceAreaForm.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Area Name</FormLabel>
                              <FormControl>
                                <Input placeholder="New York City" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={serviceAreaForm.control}
                          name="description"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Description (optional)</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Manhattan, Brooklyn, Queens, and surrounding areas"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={serviceAreaForm.control}
                          name="is_primary"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                              <div className="space-y-0.5">
                                <FormLabel>Primary Area</FormLabel>
                                <FormDescription>
                                  Mark this as your main service area
                                </FormDescription>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <div className="flex gap-2">
                          <Button type="submit" disabled={addServiceAreaMutation.isPending}>
                            {addServiceAreaMutation.isPending && (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            Add Area
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setAddingArea(false)
                              serviceAreaForm.reset()
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </form>
                    </Form>
                  </CardContent>
                </Card>
              )}

              {serviceAreas && serviceAreas.length > 0 ? (
                <div className="space-y-3">
                  {serviceAreas.map((area) => (
                    <div
                      key={area.id}
                      className="flex items-center justify-between rounded-lg border p-4"
                    >
                      <div className="flex items-center gap-3">
                        <MapPin className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{area.name}</span>
                            {area.is_primary && (
                              <Badge variant="secondary" className="gap-1">
                                <Star className="h-3 w-3" />
                                Primary
                              </Badge>
                            )}
                          </div>
                          {area.description && (
                            <p className="text-sm text-muted-foreground">{area.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!area.is_primary && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => togglePrimaryMutation.mutate(area.id)}
                            disabled={togglePrimaryMutation.isPending}
                          >
                            Set Primary
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => deleteServiceAreaMutation.mutate(area.id)}
                          disabled={deleteServiceAreaMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : !addingArea ? (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12">
                  <MapPin className="mb-4 h-12 w-12 text-muted-foreground/50" />
                  <p className="text-muted-foreground">No service areas added yet</p>
                  <Button
                    variant="link"
                    onClick={() => setAddingArea(true)}
                    className="mt-2"
                  >
                    Add your first service area
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Website Tab */}
        <TabsContent value="website">
          <WebsitePage />
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding">
          <BrandingPage />
        </TabsContent>
      </Tabs>
    </div>
  )
}
